import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  fetchAdminWorkspaces,
  getAdminUser,
  refreshAdminSession,
  setAdminSession,
  switchAdminWorkspace,
} from "../api/adminClient";
import { ROUTES } from "./routes";
import "./selectProfile.css";

function ProfilePickerCard({ org, busy, onSelect }) {
  const name = org.tenant_name || "Workspace";
  const role = org.role_name || "Admin";
  const current = org.is_current;

  return (
    <li>
      <button
        type="button"
        disabled={busy}
        onClick={() => onSelect(org)}
        className={`admin-profile-picker-card${current ? " admin-profile-picker-card--current" : ""}`}
        aria-current={current ? "true" : undefined}
      >
        <div className="admin-profile-picker-logo-ring">
          {org.tenant_logo ? (
            <img src={org.tenant_logo} alt="" className="admin-profile-picker-logo" />
          ) : (
            <span className="admin-profile-picker-logo admin-profile-picker-logo--placeholder" aria-hidden>
              {name.charAt(0).toUpperCase()}
            </span>
          )}
        </div>
        <h2 className="admin-profile-picker-org">{name}</h2>
        <div className="admin-profile-picker-bar" aria-hidden />
        <p className="admin-profile-picker-role">
          Role: <span>{role}</span>
        </p>
        {current ? (
          <span className="admin-profile-picker-badge">Current</span>
        ) : (
          <span className="admin-profile-picker-enter" aria-hidden>
            {busy ? "…" : "→"}
          </span>
        )}
      </button>
    </li>
  );
}

export default function SwitchWorkspacePage() {
  const navigate = useNavigate();
  const session = getAdminUser();
  const [loading, setLoading] = useState(true);
  const [switchingId, setSwitchingId] = useState("");
  const [organizations, setOrganizations] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchAdminWorkspaces();
      setOrganizations(res.data?.organizations || []);
    } catch (err) {
      toast.error(err.message || "Could not load workspaces");
      setOrganizations([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSwitch = async (org) => {
    if (org.is_current || org.user_id === String(session?.id || session?._id || "")) {
      toast.success("You are already in this workspace");
      return;
    }
    setSwitchingId(org.user_id);
    try {
      const res = await switchAdminWorkspace({ user_id: org.user_id });
      const token = res.data?.token;
      const user = res.data?.user;
      if (!token || !user) throw new Error("Invalid switch response");
      user.name = `${user.first_name || ""} ${user.last_name || ""}`.trim();
      setAdminSession(token, user);
      await refreshAdminSession();
      toast.success(`Opened ${org.tenant_name || "workspace"}`);
      navigate(ROUTES.dashboard, { replace: true });
    } catch (err) {
      toast.error(err.message || "Could not switch workspace");
    } finally {
      setSwitchingId("");
    }
  };

  return (
    <div className="admin-profile-picker-page">
      <header className="admin-profile-picker-header">
        <h1 className="admin-profile-picker-title">Select your profile</h1>
        <p className="admin-profile-picker-sub">
          Signed in as <strong>{session?.email || "your account"}</strong>. Choose the organization
          to open.
        </p>
      </header>

      {loading ? (
        <p className="admin-profile-picker-muted">Loading profiles…</p>
      ) : organizations.length === 0 ? (
        <p className="admin-profile-picker-muted">No workspaces found for your account.</p>
      ) : (
        <ul className="admin-profile-picker-grid">
          {organizations.map((org) => (
            <ProfilePickerCard
              key={org.user_id}
              org={org}
              busy={switchingId === org.user_id}
              onSelect={handleSwitch}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
